import { useCallback, useEffect, useRef } from 'react';

/** Layout/visibility evidence, used after a paint opportunity, never on mount. */
export const isTrialNoticeVisible = (element: HTMLElement | null): boolean => {
  if (!element?.isConnected || document.visibilityState !== 'visible') return false;
  const rect = element.getBoundingClientRect();
  if (
    rect.width <= 0 ||
    rect.height <= 0 ||
    rect.bottom <= 0 ||
    rect.right <= 0 ||
    rect.top >= window.innerHeight ||
    rect.left >= window.innerWidth
  )
    return false;
  for (let parent: HTMLElement | null = element; parent; parent = parent.parentElement) {
    const style = getComputedStyle(parent);
    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      style.visibility === 'collapse' ||
      style.opacity === '0'
    )
      return false;
  }
  return true;
};

/** A canceled or hidden render never reports display. User interaction also
 * proves display when dismissal precedes the second animation frame. */
export const useTrialNoticeShown = (onShown: (() => void) | undefined) => {
  const ref = useRef<HTMLDivElement>(null);
  const reported = useRef(false);
  const reportVisible = useCallback(() => {
    if (!reported.current && onShown && isTrialNoticeVisible(ref.current)) {
      reported.current = true;
      onShown();
    }
  }, [onShown]);

  useEffect(() => {
    if (!onShown) return;
    let first: number | undefined;
    let second: number | undefined;
    const cancelFrames = () => {
      if (first !== undefined) cancelAnimationFrame(first);
      if (second !== undefined) cancelAnimationFrame(second);
    };
    const scheduleVisibleReceipt = () => {
      cancelFrames();
      if (reported.current || document.visibilityState !== 'visible') return;
      first = requestAnimationFrame(() => {
        second = requestAnimationFrame(reportVisible);
      });
    };
    // Returning to this same mounted dialog can prove its first display. This
    // schedules only the receipt, never another display authorization or render.
    document.addEventListener('visibilitychange', scheduleVisibleReceipt);
    scheduleVisibleReceipt();
    return () => {
      cancelFrames();
      document.removeEventListener('visibilitychange', scheduleVisibleReceipt);
    };
  }, [onShown, reportVisible]);

  return { ref, reportVisible };
};
